import React, { useState, useEffect } from 'react';
import { userAppContext } from '../../Context/Context';
import { validateEmailAcademic, validateEmailBusiness } from '../Support/ValidationLogic';
import PricePlanesAcademic from '../Pricings/PricePlanesAcademic';
import AdvancedPlanOnly from '../Pricings/AdvancedPlanOnly';
import Swal from 'sweetalert2';
import AcademicLicensePopUp from '../SignUp/AcademicLicensePopUp';
import AcademicApplicationService from '../../services/AcademicApplicationService';

const UpgradePlan = () => {
  const { user, groups = [] } = userAppContext();
  const isTrial = groups?.includes('ROLE_TRIAL');
  const isAcademic = groups?.includes('ROLE_ACADEMIC');
  const isStandard = groups?.includes('ROLE_STANDARD');

  const isAcademicValid = validateEmailAcademic(user?.email);
  const isBusinessValid = validateEmailBusiness(user?.email);

  const [showModal, setShowModal] = useState(false);
  const [showModalAcademic, setShowModalAcademic] = useState(false);
  const [showModalBusiness, setShowModalBusiness] = useState(false);
  const [showAcademicLicensePopup, setShowAcademicLicensePopup] = useState(false);
  const [academicLicenseAccepted, setAcademicLicenseAccepted] = useState(false);
  const [formErrorMessage, setFormErrorMessage] = useState('');
  const [academicStatus, setAcademicStatus] = useState(null);
  const [trialType, setTrialType] = useState(null);
  const [activeCampaignLists, setActiveCampaignLists] = useState([]);
  const [loadingStatus, setLoadingStatus] = useState(true);

  // Get academic status on mount
  useEffect(() => {
    const fetchAcademicStatus = async () => {
      if (user?.email) {
        try {
          const response = await AcademicApplicationService.getAcademicStatus(user.email);

          setAcademicStatus(response.status);
          setTrialType(response.trialType);
          setActiveCampaignLists(response.activeCampaignLists || []);
        } catch (error) {
          console.error('Error fetching academic status:', error);
        } finally {
          setLoadingStatus(false);
        }
      } else {
        setLoadingStatus(false);
      }
    };

    fetchAcademicStatus();
  }, [user?.email]);

  useEffect(() => {
    if (!user?.email || !isTrial || academicStatus !== 'pending') {
      return;
    }

    const pollInterval = setInterval(async () => {
      try {
        const response = await AcademicApplicationService.getAcademicStatus(user.email);

        if (response.groups?.includes('ROLE_ACADEMIC')) {
          clearInterval(pollInterval);
          window.location.reload();
        } else if (response.status !== academicStatus) {
          setAcademicStatus(response.status);
        }
      } catch (error) {
        console.error('Error polling academic status:', error);
      }
    }, 30000);

    return () => clearInterval(pollInterval);
  }, [user?.email, isTrial, academicStatus]);

  const handleUpgradeClick = () => setShowModal(true);

  const handleCloseModal = () => {
    setShowModal(false) || setShowModalAcademic(false) || setShowModalBusiness(false);
    setFormErrorMessage('');
  };

  const handleApplyToAcademicClick = () => setShowModalAcademic(true);

  const validateSubmit = () => {
    if (!academicLicenseAccepted) {
      setFormErrorMessage('You must accept the Disgenet Academic License before submitting.');
      return false;
    }
    setFormErrorMessage('');
    return true;
  };

  const handleAcademicLicenseCheckboxChange = e => {
    setAcademicLicenseAccepted(e.target.checked);
    if (e.target.checked) {
      setFormErrorMessage('');
    }
  };

  const handleAcademicLicensePopup = () => {
    setShowAcademicLicensePopup(true);
  };

  const handleSubmit = e => {
    e.preventDefault();
    // No longer need to show logout modal - Stripe handles the upgrade flow
    setShowModal(false);
  };

  const handleAcademicSubmit = async e => {
    e.preventDefault();

    // Normal application flow
    if (!validateSubmit()) {
      return;
    }

    setShowModalAcademic(false);

    // Show loading
    Swal.fire({
      title: 'Submitting your application...',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const response = await AcademicApplicationService.applyForAcademic(user?.email);

      if (response.status === 'error') {
        Swal.fire({
          title: 'Error',
          text: response.message,
          icon: 'error',
          customClass: {
            confirmButton: 'btn-gradient-pink',
          },
        });
      } else {
        Swal.fire({
          title: 'Application Submitted!',
          text:
            response.message ||
            'Thank you for submitting your request. Someone will review it within 7 days.',
          icon: 'success',
          customClass: {
            confirmButton: 'btn-gradient-blue',
          },
        });
        // Refresh status
        const statusResponse = await AcademicApplicationService.getAcademicStatus(user?.email);
        setAcademicStatus(statusResponse.status);
      }
    } catch (error) {
      Swal.fire({
        title: 'Something went wrong',
        html: 'An error occurred while submitting your application. Please <a href="/Support" style="color: #667eea; text-decoration: underline;">contact us</a> for assistance.',
        icon: 'error',
        showCancelButton: true,
        confirmButtonText: 'Contact Us',
        cancelButtonText: 'Close',
        customClass: {
          confirmButton: 'btn-gradient-blue',
          cancelButton: 'btn-gradient-pink',
        },
      }).then(result => {
        if (result.isConfirmed) {
          window.location.href = '/Support';
        }
      });
      setShowModalAcademic(false);
    }
  };

  const handleConfirmBusiness = () => {
    setShowModalBusiness(true);
  };

  const handleBusinessSubmit = async e => {
    e.preventDefault();
    setShowModalBusiness(false);

    const messageInfo = {
      name: user?.['custom:name'] || 'Unknown',
      email: user?.email || 'Unknown',
      organization: user?.['custom:company'] || 'Unknown',
      role: user?.['custom:application_role'] || 'Unknown',
      message:
        "This user has completed a free trial. They have clicked the 'request pricing' button inside their account and would like to receive a quotation",
      subject: 'TRIAL EXPIRED: Request for commercial pricing',
    };

    try {
      const response = await fetch('https://p3xqewey7e.execute-api.eu-central-1.amazonaws.com/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(messageInfo),
      });
      if (response.ok) {
        Swal.fire({
          title: 'Your request has been sent',
          customClass: {
            confirmButton: 'btn-gradient-blue',
          },
        });
      } else {
        console.error('Error en la petición:', response.statusText);
        Swal.fire('Oops! Something went wrong.');
      }
    } catch (e) {
      console.error(e);
      Swal.fire('Error with your request. Try again later.');
    }
    setShowModalAcademic(false);
  };

  let buttonsToRender = null;

  if (process.env.REACT_APP_ENABLE_STRIPE_PAYMENTS === 'true') {
    if (loadingStatus) {
      buttonsToRender = <div>Loading...</div>;
    } else if (isTrial && trialType === 'academic') {
      if (academicStatus === 'pending') {
        buttonsToRender = (
          <div>
            <p className="text-muted">Your academic application is pending approval</p>
          </div>
        );
      } else if (academicStatus === 'approved') {
        buttonsToRender = (
          <div>
            <p className="text-muted">Your academic application has been approved</p>
          </div>
        );
      } else {
        buttonsToRender = (
          <div>
            <button className="btn-gradient-blue" onClick={handleApplyToAcademicClick}>
              Apply for Free Academic Plan
            </button>
          </div>
        );
      }
    } else if (isTrial && trialType === 'commercial') {
      buttonsToRender = (
        <div>
          <button className="btn-gradient-blue">
            <a
              href="https://calendly.com/medbioinformatics/disgenet-demo?month=2025-07"
              target="_blank"
              className="text-white"
              rel="noreferrer"
            >
              Schedule a demo
            </a>
          </button>
          <button className="btn-gradient-pink" onClick={handleConfirmBusiness}>
            Request Pricing
          </button>
        </div>
      );
    } else if (isTrial && isAcademicValid) {
      if (academicStatus === 'pending') {
        buttonsToRender = (
          <div>
            <p className="text-muted">Your academic application is pending approval</p>
            <button className="btn-gradient-pink" onClick={handleUpgradeClick}>
              Upgrade your Plan
            </button>
          </div>
        );
      } else if (academicStatus === 'approved') {
        buttonsToRender = (
          <div>
            <button className="btn-gradient-blue" onClick={handleApplyToAcademicClick}>
              Pay for Academic Plan
            </button>
            <button className="btn-gradient-pink" onClick={handleUpgradeClick}>
              Upgrade your Plan
            </button>
          </div>
        );
      } else {
        buttonsToRender = (
          <div>
            <button className="btn-gradient-blue" onClick={handleApplyToAcademicClick}>
              Apply for Free Academic Plan
            </button>
            <button className="btn-gradient-pink" onClick={handleUpgradeClick}>
              Upgrade your Plan
            </button>
          </div>
        );
      }
    } else if (isTrial && isBusinessValid) {
      buttonsToRender = (
        <div>
          <button className="btn-gradient-blue">
            <a
              href="https://calendly.com/medbioinformatics/disgenet-demo?month=2025-07"
              target="_blank"
              className="text-white"
              rel="noreferrer"
            >
              Schedule a demo
            </a>
          </button>
          <button className="btn-gradient-pink" onClick={handleBusinessSubmit}>
            Request Pricing
          </button>
        </div>
      );
    } else if (!isTrial && (isAcademic || isStandard)) {
      const canSeePricing = activeCampaignLists.includes('12') || activeCampaignLists.includes('21');

      if (canSeePricing) {
        buttonsToRender = (
          <div>
            <button className="btn-gradient-pink" onClick={handleUpgradeClick}>
              Upgrade your Plan
            </button>
          </div>
        );
      } else {
        // User cannot see pricing - show contact message
        buttonsToRender = (
          <div>
            <button className="btn-gradient-blue">
              <a href="/Support" className="text-white">
                Contact Us
              </a>
            </button>
          </div>
        );
      }
    } else {
      // Fallback - no condition matched
      console.warn('No button condition matched. User state:', {
        isTrial,
        isAcademic,
        isStandard,
        trialType,
        academicStatus,
      });
    }
  }

  return (
    <div>
      {buttonsToRender}

      {/* Modal */}
      {showModal &&
        (() => {
          const canSeePricing = activeCampaignLists.includes('12') || activeCampaignLists.includes('21');

          return (
            <div className="modal-overlay">
              <div className="popup-upgrade">
                <button className="modal-close-btn" onClick={handleCloseModal}>
                  x
                </button>

                <h3 className="text-center">Upgrade Your Plan</h3>

                {!canSeePricing ? (
                  <div className="text-center p-4">
                    <p className="mb-4">To view pricing and upgrade options, please contact us.</p>
                    <button className="btn-gradient-blue" onClick={() => (window.location.href = '/Support')}>
                      Contact Us
                    </button>
                  </div>
                ) : isStandard ? (
                  <AdvancedPlanOnly />
                ) : process.env.REACT_APP_SHOW_ACADEMIC_PRICES === 'true' ? (
                  <>
                    <PricePlanesAcademic />
                    <p className="text-muted text-center mt-3" style={{ fontSize: '0.85em' }}>
                      Not academic?{' '}
                      <a href="/Support" style={{ color: '#667eea', textDecoration: 'underline' }}>
                        Contact us
                      </a>
                      .
                    </p>
                  </>
                ) : (
                  <form onSubmit={handleSubmit}>
                    <div>
                      <div>
                        <label>Name:</label>
                        <input type="text" name="name" value={user?.['custom:name'] || ''} readOnly />
                      </div>
                      <div>
                        <label>Email:</label>
                        <input type="email" name="email" value={user?.email || ''} readOnly />
                      </div>
                      <div>
                        <label>Select Plan:</label>
                        <select
                          name="selectedPlan"
                          defaultValue=""
                          onChange={e => console.log('Selected:', e.target.value)}
                        >
                          <option value="">...</option>
                          <option value="Standard">Standard</option>
                          <option value="Professional">Professional</option>
                          <option value="Premium">Premium</option>
                        </select>
                      </div>
                      <div className="d-flex justify-content-center align-items-center">
                        <button type="submit" className="btn-gradient-pink">
                          Apply
                        </button>
                      </div>
                      <p className="text-muted text-center mt-3" style={{ fontSize: '0.85em' }}>
                        Not academic?{' '}
                        <a href="/Support" style={{ color: '#667eea', textDecoration: 'underline' }}>
                          Contact us
                        </a>
                        .
                      </p>
                    </div>
                  </form>
                )}
              </div>
            </div>
          );
        })()}

      {/* Modal Académico */}
      {showModalAcademic && (
        <div className="modal-overlay">
          <div className="RegisterPopup">
            <div className="apply-popup-container">
              <button className="modal-close-btn" onClick={handleCloseModal}>
                x
              </button>

              {academicStatus === 'approved' ? (
                <>
                  <h4>Your academic application has been approved!</h4>
                  <p>Complete your payment to activate your Academic plan.</p>
                  <button
                    onClick={handleAcademicSubmit}
                    className="btn-gradient-blue"
                    aria-label="submit-modal"
                  >
                    Proceed to Payment
                  </button>
                  <button onClick={handleCloseModal} className="btn-gradient-pink" aria-label="cancel-modal">
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <h4>Submit your application for the Free Academic Plan</h4>
                  <p>Your application will be reviewed before approval.</p>

                  <div className="form-check mb-3">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={academicLicenseAccepted}
                      onChange={handleAcademicLicenseCheckboxChange}
                      id="academicLicenseCheckbox"
                    />
                    <label htmlFor="academicLicenseCheckbox">
                      I accept the{' '}
                      <button
                        type="button"
                        onClick={handleAcademicLicensePopup}
                        style={{ borderBottom: '2px solid rgb(192,195,202)' }}
                      >
                        DISGENET Academic License
                      </button>
                    </label>
                  </div>

                  <button
                    onClick={handleAcademicSubmit}
                    className="btn-gradient-blue"
                    aria-label="submit-modal"
                  >
                    Submit
                  </button>

                  <button onClick={handleCloseModal} className="btn-gradient-pink" aria-label="cancel-modal">
                    Cancel
                  </button>
                  {formErrorMessage && <p className="form-error mt-4">{formErrorMessage}</p>}

                  <p className="text-muted mt-3" style={{ fontSize: '0.85em' }}>
                    Not academic?{' '}
                    <a href="/Support" style={{ color: '#667eea', textDecoration: 'underline' }}>
                      Contact us
                    </a>
                    .
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Business */}
      {showModalBusiness && (
        <div className="modal-overlay">
          <div className="RegisterPopup">
            <div className="apply-popup-container">
              <button className="modal-close-btn" onClick={handleCloseModal}>
                x
              </button>
              <>
                <h4 className="m-2">Are you sure you want to send a request for pricing?</h4>
                <p className="mt-4 text-base">Our team will get back to you shortly.</p>
                <button
                  onClick={handleBusinessSubmit}
                  className="btn-gradient-blue"
                  aria-label="submit-modal"
                >
                  Send Request
                </button>
                <button onClick={handleCloseModal} className="btn-gradient-pink" aria-label="cancel-modal">
                  Cancel
                </button>
              </>
            </div>
          </div>
        </div>
      )}

      {showAcademicLicensePopup && (
        <AcademicLicensePopUp
          onClose={() => setShowAcademicLicensePopup(false)}
          onAcceptTerms={() => setAcademicLicenseAccepted(true)}
        />
      )}
    </div>
  );
};

export default UpgradePlan;
